import { useCallback, useEffect } from 'react';
import { appActions } from '../../app/store/appActions';
import { useAuthStore } from '../../app/store/useAuthStore';
import { useUiStore } from '../../app/store/useUiStore';
import { useWorkspaceStore } from '../../app/store/useWorkspaceStore';
import { applyAccentTokens, applyDesignTokens } from '../constants/designTokens';
import type { AccentType } from '../types/AccentType';
import type { ResolvedTheme, ThemePreference } from '../types/ThemeType';
import { getSystemTheme } from '../utils/getSystemTheme';
import {
  resolveThemePreference,
  THEME_CHECK_INTERVAL_MS,
} from '../utils/resolveThemePreference';

const useTheme = (): [
  isEnabled: boolean,
  onModeChange: (mode: ResolvedTheme) => void,
  isAuto: boolean,
  setIsAuto: (isAuto: boolean) => void,
  accent: AccentType,
  setAccent: (accent: AccentType) => void,
] => {
  const mode = useUiStore((state) => state.mode);
  const themePreference = useUiStore((state) => state.themePreference);
  const themeSchedule = useUiStore((state) => state.themeSchedule);
  const isAuto = useUiStore((state) => state.isAuto);
  const accent = useUiStore((state) => state.accent);
  const setMode = useUiStore((state) => state.setMode);
  const setIsAuto = useUiStore((state) => state.setIsAuto);
  const setAccent = useUiStore((state) => state.setAccent);

  const isEnabled = mode === 'dark';

  const onModeChange = useCallback((nextMode: ResolvedTheme) => {
    setMode(nextMode);
    setIsAuto(false);
  }, [setIsAuto, setMode]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const root = window.document.documentElement;
    const resolvedTheme: ResolvedTheme = isEnabled ? 'dark' : 'light';

    root.classList.remove('light', 'dark');
    root.classList.add(resolvedTheme);
    root.setAttribute('data-theme', resolvedTheme);

    applyDesignTokens(resolvedTheme, root);
    applyAccentTokens(accent, resolvedTheme, root);
  }, [isEnabled, accent]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const storesHydrated = () =>
      useAuthStore.persist.hasHydrated()
      && useWorkspaceStore.persist.hasHydrated()
      && useUiStore.persist.hasHydrated();

    const syncWorkspaceUi = () => {
      if (storesHydrated()) {
        appActions.syncWorkspaceUi();
      }
    };

    syncWorkspaceUi();

    const unsubAuth = useAuthStore.persist.onFinishHydration(syncWorkspaceUi);
    const unsubWorkspace = useWorkspaceStore.persist.onFinishHydration(syncWorkspaceUi);
    const unsubUi = useUiStore.persist.onFinishHydration(syncWorkspaceUi);

    return () => {
      unsubAuth();
      unsubWorkspace();
      unsubUi();
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !isAuto || themePreference !== 'system') {
      return;
    }

    const applySystemTheme = () => {
      setMode(getSystemTheme());
    };

    applySystemTheme();

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleSystemThemeChange = () => {
      const { isAuto: auto, themePreference: preference } = useUiStore.getState();
      if (auto && preference === 'system') {
        applySystemTheme();
      }
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);

    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, [isAuto, themePreference, setMode]);

  useEffect(() => {
    if (typeof window === 'undefined' || themePreference !== 'schedule') {
      return;
    }

    const applyScheduledTheme = () => {
      const resolved = resolveThemePreference({
        preference: 'schedule',
        schedule: themeSchedule,
        systemDark: window.matchMedia('(prefers-color-scheme: dark)').matches,
      });

      if (useUiStore.getState().mode !== resolved) {
        setMode(resolved);
      }
    };

    applyScheduledTheme();

    const intervalId = window.setInterval(applyScheduledTheme, THEME_CHECK_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        applyScheduledTheme();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [themePreference, themeSchedule, setMode]);

  return [isEnabled, onModeChange, isAuto, setIsAuto, accent, setAccent];
};

export default useTheme;

export const resolveThemePreferenceForPreview = (
  preference: ThemePreference,
  schedule?: { lightFrom: string; darkFrom: string }
): ResolvedTheme =>
  resolveThemePreference({
    preference,
    schedule,
    systemDark: typeof window !== 'undefined'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
      : false,
  });
